import moment from 'moment';
import BaseDetails from '@/store/modules/base.details';
import compress from '@/utilities/compress';
import { ServiceAssignment } from '@/types';
import { AsyncModule } from 'vuex-async-mutations';

/** Matches the CRM's pdf.photo_max_edge, so the PDF embeds no less than it does today. */
const PHOTO_MAX_EDGE = 2560;

/** Longest a decode+re-encode may take before the original file is uploaded instead. */
const COMPRESS_TIMEOUT_MS = 15000;

/** compress() re-encodes to JPEG but keeps the original name, so the extension has to follow. */
function jpegName(name: string): string {
  return `${name.replace(/\.[^./\\]*$/, '')}.jpg`;
}

/**
 * compress() never settles when the browser cannot decode the file — it has no
 * `img.onerror`, and its `reader.onerror` is registered inside `reader.onload`.
 * Uploading the original is always valid, so never let that stall the upload.
 */
function compressForUpload(file: File): Promise<File> {
  let timer: ReturnType<typeof setTimeout>;

  // Promise.resolve().then() so a synchronous throw (no FileReader) also falls back.
  const attempt = Promise.resolve()
    .then(() => compress(file, { maxWidth: PHOTO_MAX_EDGE, maxHeight: PHOTO_MAX_EDGE }))
    .catch(() => file);

  const expiry = new Promise<File>(resolve => {
    timer = setTimeout(() => resolve(file), COMPRESS_TIMEOUT_MS);
  });

  return Promise.race<File>([attempt, expiry]).finally(() => clearTimeout(timer));
}

interface ServiceState {
  selectedDate: number | null;
  list: ServiceAssignment[];
}

export const module = {
  namespaced: true,

  async: true,

  state: {
    selectedDate: null,
    list: [],
  },

  modules: {
    // → fetch(id) GET /service/assignment/{id}, details(id) getter, item:update/item:delete
    details: BaseDetails<ServiceAssignment>('/service/assignment', 'id'),
  },

  getters: {
    ['selectedDate'](state) {
      return state.selectedDate ?? Date.now();
    },

    // Client-side day filter — the backend already returns only start_at NOT NULL.
    ['byDate'](state) {
      return (timestamp: number) => {
        const day = moment(timestamp);

        return state.list.filter(
          assignment => assignment.start_at && moment(assignment.start_at).isSame(day, 'day'),
        );
      };
    },
  },

  mutations: {
    ['setDate'](state, date: number) {
      state.selectedDate = date;
    },
  },

  actions: {
    ['saveDraft'](_ctx, { id, data }: { id: number; data: Record<string, any> }) {
      return this.$axios.$put(`/service/assignment/${id}/questionnaire`, data);
    },

    async ['uploadPhoto'](_ctx, { id, slot, file }: { id: number; slot: string; file: File }) {
      // Phones send 12MP originals. Shrinking here rather than at render time cuts
      // the upload over mobile and what S3 stores, and drops the EXIF orientation
      // tag that would otherwise force Chromium to rasterise the photo into the PDF.
      const compressed = await compressForUpload(file);

      const formData = new FormData();
      formData.append('slot', slot);
      // compress() returns the original file untouched when it is already small enough.
      formData.append('file', compressed, compressed === file ? file.name : jpegName(file.name));

      return this.$axios.$post(`/service/assignment/${id}/photo`, formData, {
        // `false` lets axios set the multipart boundary itself.
        headers: { ['Content-Type']: false },
      });
    },

    ['deletePhoto'](_ctx, { id, doc }: { id: number; doc: number }) {
      return this.$axios.$delete(`/service/assignment/${id}/photo/${doc}`);
    },

    ['submit'](_ctx, { id, data }: { id: number; data: Record<string, any> }) {
      return this.$axios.$post(`/service/assignment/${id}/submit`, data);
    },
  },

  actionsAsync: {
    ['fetch:list']: {
      handler({ commitAsync }) {
        return commitAsync(
          'fetch:list',
          this.$axios.$get<ServiceAssignment[]>('/service/assignments'),
        );
      },

      resolved(state, items: ServiceAssignment[]) {
        state.list = items ?? [];
      },
    },
  },
} as AsyncModule<ServiceState, any>;

export default module;
